import { Apple, ArrowRight, Download, Share, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { useI18nStore } from "../../stores/i18nStore";
import { APK_URL, androidInstallUrl, siteUrl } from "./appInstall";
import { StepList } from "./StepList";

/**
 * "Get the app" section on the landing page.
 *
 * ANDROID: HealthPass has no Android build pipeline yet - no Capacitor, no
 * Bubblewrap/TWA config, and no .apk anywhere in the repo. Rather than ship a
 * download button pointing at a URL that 404s, the APK location comes from
 * `VITE_ANDROID_APK_URL` and the card states plainly that the download is not
 * available yet when it is unset. Set the variable once an APK is published and
 * the button and its QR code start working with no code change.
 *
 * iOS: Apple allows no third-party app stores or sideloading here, so the
 * iPhone path is the PWA install, which only Safari can perform - Chrome and
 * Firefox on iOS have no "Add to Home Screen". The steps say so explicitly,
 * because silently failing in the wrong browser is the most common way this
 * goes wrong.
 */

export function AppInstallSection() {
  const { t } = useI18nStore();

  const androidSteps = [
    t.appInstallAndroidStep1,
    t.appInstallAndroidStep2,
    t.appInstallAndroidStep3,
    t.appInstallAndroidStep4,
    t.appInstallAndroidStep5,
  ];

  // Without a published APK the Android card would otherwise hold a notice and
  // nothing else. Chrome installs the same PWA today, so that path gets a full
  // guide of its own rather than leaving Android users with no instructions.
  const androidPwaSteps = [
    t.appInstallAndroidPwaStep1,
    t.appInstallAndroidPwaStep2,
    t.appInstallAndroidPwaStep3,
    t.appInstallAndroidPwaStep4,
    t.appInstallAndroidPwaStep5,
  ];

  const iosSteps = [
    t.appInstallIosStep1,
    t.appInstallIosStep2,
    t.appInstallIosStep3,
    t.appInstallIosStep4,
    t.appInstallIosStep5,
  ];

  return (
    <section
      id="get-the-app"
      className="scroll-mt-24 bg-white px-4 py-20 sm:px-6 lg:px-8"
      aria-labelledby="get-the-app-title"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h2
            id="get-the-app-title"
            className="mb-4 text-4xl font-bold text-gray-900"
          >
            {t.appInstallTitle}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            {t.appInstallSubtitle}
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* ── Android ─────────────────────────────────────────────── */}
          <div className="flex flex-col rounded-2xl border border-gray-200 bg-gray-50 p-8">
            <div className="mb-6 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                <Smartphone className="h-6 w-6 text-green-700" />
              </span>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t.appInstallAndroidTitle}
                </h3>
                <p className="text-sm text-gray-600">
                  {t.appInstallAndroidSubtitle}
                </p>
              </div>
            </div>

            {APK_URL ? (
              <>
                <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row">
                  <div
                    className="keep-light rounded-xl border-2 border-gray-200 bg-white p-3"
                    data-testid="android-qr"
                  >
                    <QRCode value={APK_URL} size={128} level="M" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <p className="mb-3 text-sm text-gray-600">
                      {t.appInstallScanToDownload}
                    </p>
                    <a
                      href={APK_URL}
                      className="inline-flex items-center gap-2 rounded-lg bg-green-700 px-5 py-3 font-semibold text-white transition hover:bg-green-800"
                      data-testid="apk-download-link"
                    >
                      <Download className="h-5 w-5" aria-hidden="true" />
                      {t.appInstallDownloadApk}
                    </a>
                  </div>
                </div>
                <StepList steps={androidSteps} />
              </>
            ) : (
              <>
                <div
                  className="mb-6 flex flex-col items-center gap-4 sm:flex-row"
                  data-testid="android-pwa-qr-row"
                >
                  <div className="keep-light rounded-xl border-2 border-gray-200 bg-white p-3">
                    <QRCode value={androidInstallUrl()} size={128} level="M" />
                  </div>
                  <div
                    className="flex-1 rounded-xl border border-amber-200 bg-amber-50 p-4"
                    data-testid="apk-unavailable"
                  >
                    <p className="mb-1 font-semibold text-amber-900">
                      {t.appInstallApkUnavailable}
                    </p>
                    <p className="text-sm text-amber-800">
                      {t.appInstallApkUnavailableHint}
                    </p>
                  </div>
                </div>

                <h4 className="mb-3 font-semibold text-gray-900">
                  {t.appInstallAndroidPwaTitle}
                </h4>
                <StepList steps={androidPwaSteps} />
              </>
            )}

            <Link
              to="/android-install"
              className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-green-800 hover:underline"
              data-testid="android-guide-link"
            >
              {t.appInstallAndroidGuideLink}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          {/* ── iOS ─────────────────────────────────────────────────── */}
          <div className="flex flex-col rounded-2xl border border-gray-200 bg-gray-50 p-8">
            <div className="mb-6 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-200">
                <Apple className="h-6 w-6 text-gray-800" />
              </span>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t.appInstallIosTitle}
                </h3>
                <p className="text-sm text-gray-600">
                  {t.appInstallIosSubtitle}
                </p>
              </div>
            </div>

            <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row">
              <div
                className="keep-light rounded-xl border-2 border-gray-200 bg-white p-3"
                data-testid="ios-qr"
              >
                <QRCode value={siteUrl()} size={128} level="M" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-sm text-gray-600">
                  {t.appInstallScanToOpen}
                </p>
              </div>
            </div>

            <StepList steps={iosSteps} />

            <p className="mt-6 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
              <Share className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t.appInstallIosSafariNote}</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
